/**
 * auth.js — NexChat Authentication
 *
 * Handles OIDC login via MAS (Matrix Authentication Service),
 * token storage, refresh, and session restore.
 *
 * Design decisions:
 * - PKCE flow for security (no client secret exposed in browser)
 * - Tokens stored in localStorage with expiry metadata
 * - Silent token refresh 60s before expiry
 * - Device ID is stable per browser (generated once, persisted)
 */

const LS = {
  ACCESS_TOKEN:   'nx_access_token',
  REFRESH_TOKEN:  'nx_refresh_token',
  EXPIRES_AT:     'nx_expires_at',
  DEVICE_ID:      'nx_device_id',
  USER_ID:        'nx_user_id',
  PKCE_VERIFIER:  'nx_pkce_verifier',
  PKCE_STATE:     'nx_pkce_state',
};

// Filled by config in main.js
let CONFIG = {
  homeserverUrl: '',
  masUrl: '',
  clientId: '',
  redirectUri: '',
};

let _refreshTimer = null;
let _onTokenRefreshed = null; // callback → (accessToken) notifies matrix.js

export function configureAuth(cfg) {
  CONFIG = { ...CONFIG, ...cfg };
}

export function setTokenRefreshCallback(cb) {
  _onTokenRefreshed = cb;
}

/* ──────────────────────────────────────────────
   Device ID — stable per browser session
─────────────────────────────────────────────── */
export function getDeviceId() {
  let id = localStorage.getItem(LS.DEVICE_ID);
  if (!id) {
    id = 'NX_' + Math.random().toString(36).slice(2, 10).toUpperCase() +
         '_' + Date.now().toString(36).toUpperCase();
    localStorage.setItem(LS.DEVICE_ID, id);
  }
  return id;
}

/* ──────────────────────────────────────────────
   Token storage helpers
─────────────────────────────────────────────── */
export function saveTokens({ access_token, refresh_token, expires_in }) {
  const expiresAt = Date.now() + (expires_in || 3600) * 1000;
  localStorage.setItem(LS.ACCESS_TOKEN, access_token);
  if (refresh_token) localStorage.setItem(LS.REFRESH_TOKEN, refresh_token);
  localStorage.setItem(LS.EXPIRES_AT, String(expiresAt));
}

export function getStoredTokens() {
  return {
    accessToken:  localStorage.getItem(LS.ACCESS_TOKEN),
    refreshToken: localStorage.getItem(LS.REFRESH_TOKEN),
    expiresAt:    parseInt(localStorage.getItem(LS.EXPIRES_AT) || '0', 10),
    userId:       localStorage.getItem(LS.USER_ID),
    deviceId:     getDeviceId(),
  };
}

export function clearTokens() {
  [LS.ACCESS_TOKEN, LS.REFRESH_TOKEN, LS.EXPIRES_AT, LS.USER_ID].forEach(k =>
    localStorage.removeItem(k)
  );
  if (_refreshTimer) clearTimeout(_refreshTimer);
}

export function saveUserId(userId) {
  localStorage.setItem(LS.USER_ID, userId);
}

function isTokenValid(expiresAt) {
  return expiresAt && Date.now() < expiresAt - 10_000; // 10s buffer
}

/* ──────────────────────────────────────────────
   PKCE helpers
─────────────────────────────────────────────── */
async function generatePkce() {
  const verifier = generateRandomString(64);
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  const challenge = btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  return { verifier, challenge };
}

function generateRandomString(length) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const arr = new Uint8Array(length);
  crypto.getRandomValues(arr);
  return Array.from(arr).map(b => chars[b % chars.length]).join('');
}

/* ──────────────────────────────────────────────
   OIDC Discovery
─────────────────────────────────────────────── */
let _discoveryCache = null;
async function discover() {
  if (_discoveryCache) return _discoveryCache;
  const res = await fetch(`${CONFIG.masUrl}/.well-known/openid-configuration`);
  if (!res.ok) throw new Error('OIDC discovery failed');
  _discoveryCache = await res.json();
  return _discoveryCache;
}

/* ──────────────────────────────────────────────
   Login — redirect to MAS authorization endpoint
─────────────────────────────────────────────── */
export async function initiateLogin() {
  const discovery = await discover();
  const { verifier, challenge } = await generatePkce();
  const state = generateRandomString(32);

  sessionStorage.setItem(LS.PKCE_VERIFIER, verifier);
  sessionStorage.setItem(LS.PKCE_STATE, state);

  // Device ID must be stable per browser and included in the scope
  // so Synapse associates the token with a device for E2EE key upload
  const deviceId = getDeviceId();
  const scope = `urn:matrix:org.matrix.msc2967.client:api:* urn:matrix:org.matrix.msc2967.client:device:${deviceId}`;

  const params = new URLSearchParams({
    response_type:         'code',
    client_id:             CONFIG.clientId,
    redirect_uri:          CONFIG.redirectUri,
    scope,
    state,
    code_challenge:        challenge,
    code_challenge_method: 'S256',
    prompt:                'consent',
  });

  window.location.href = `${discovery.authorization_endpoint}?${params}`;
}

/* ──────────────────────────────────────────────
   Handle OIDC callback (on redirect back)
─────────────────────────────────────────────── */
export async function handleCallback() {
  const params = new URLSearchParams(window.location.search);
  const code  = params.get('code');
  const state = params.get('state');
  const error = params.get('error');

  if (error) throw new Error(`Auth error: ${error} — ${params.get('error_description') || ''}`);
  if (!code)  return null; // not a callback URL

  const savedState    = sessionStorage.getItem(LS.PKCE_STATE);
  const savedVerifier = sessionStorage.getItem(LS.PKCE_VERIFIER);

  if (state !== savedState) throw new Error('OAuth state mismatch — possible CSRF');

  sessionStorage.removeItem(LS.PKCE_STATE);
  sessionStorage.removeItem(LS.PKCE_VERIFIER);

  const discovery = await discover();
  const body = new URLSearchParams({
    grant_type:    'authorization_code',
    code,
    redirect_uri:  CONFIG.redirectUri,
    client_id:     CONFIG.clientId,
    code_verifier: savedVerifier,
  });

  const res = await fetch(discovery.token_endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.error_description || `Token exchange failed (${res.status})`);
  }

  const tokens = await res.json();
  saveTokens(tokens);

  // Clean URL
  window.history.replaceState({}, '', window.location.pathname);
  return tokens;
}

/* ──────────────────────────────────────────────
   Refresh access token using refresh token
─────────────────────────────────────────────── */
export async function refreshAccessToken() {
  const { refreshToken } = getStoredTokens();
  if (!refreshToken) throw new Error('No refresh token available');

  const discovery = await discover();
  const body = new URLSearchParams({
    grant_type:    'refresh_token',
    refresh_token: refreshToken,
    client_id:     CONFIG.clientId,
  });

  const res = await fetch(discovery.token_endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    if (res.status === 400 || res.status === 401) {
      clearTokens();
      throw new Error('SESSION_EXPIRED');
    }
    throw new Error(err.error_description || `Token refresh failed (${res.status})`);
  }

  const tokens = await res.json();
  saveTokens(tokens);

  if (_onTokenRefreshed) _onTokenRefreshed(tokens.access_token);
  return tokens.access_token;
}

/* ──────────────────────────────────────────────
   Auto-refresh scheduler
─────────────────────────────────────────────── */
export function scheduleTokenRefresh() {
  if (_refreshTimer) clearTimeout(_refreshTimer);

  const { expiresAt } = getStoredTokens();
  if (!expiresAt) return;

  const msUntilRefresh = (expiresAt - Date.now()) - 30_000; // 30s before expiry

  if (msUntilRefresh <= 0) {
    // Only refresh immediately if token is actually close to expiry
    const { expiresAt: exp } = getStoredTokens();
    if (exp && Date.now() > exp - 10_000) {
      refreshAccessToken().catch(handleRefreshError);
    }
    return;
  }

  _refreshTimer = setTimeout(async () => {
    try {
      await refreshAccessToken();
      scheduleTokenRefresh(); // schedule next cycle
    } catch (err) {
      handleRefreshError(err);
    }
  }, msUntilRefresh);
}

function handleRefreshError(err) {
  if (err.message === 'SESSION_EXPIRED') {
    window.dispatchEvent(new CustomEvent('nx:session-expired'));
  } else {
    console.error('[auth] Token refresh error:', err);
  }
}

/* ──────────────────────────────────────────────
   Session restore check — call on app start
─────────────────────────────────────────────── */
export async function restoreSession() {
  const { accessToken, refreshToken, expiresAt } = getStoredTokens();

  if (!accessToken && !refreshToken) return null;

  // Token is still valid
  if (accessToken && isTokenValid(expiresAt)) {
    scheduleTokenRefresh();
    return accessToken;
  }

  // Token expired — try to refresh
  if (refreshToken) {
    try {
      const newToken = await refreshAccessToken();
      scheduleTokenRefresh();
      return newToken;
    } catch (err) {
      if (err.message === 'SESSION_EXPIRED') return null;
      throw err;
    }
  }

  return null;
}

/* ──────────────────────────────────────────────
   Logout — revoke tokens + clear storage
─────────────────────────────────────────────── */
export async function logout() {
  const { accessToken } = getStoredTokens();

  try {
    const discovery = await discover();
    if (discovery.revocation_endpoint && accessToken) {
      await fetch(discovery.revocation_endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ token: accessToken, client_id: CONFIG.clientId }),
      });
    }
  } catch (_) {
    // Best-effort revocation
  }

  clearTokens();
}

